import { Category, Delete, DeleteForever, DriveFileMove, Edit, Inventory } from '@mui/icons-material'
import { Box, IconButton, Tooltip, Typography } from '@mui/material'
import axios from 'axios'
import { isAdmin } from 'chezy-inventory-backend/bin/auth/AuthenticationContext'
import { Bin } from 'chezy-inventory-backend/bin/database/types/Bin'
import { Item } from 'chezy-inventory-backend/bin/database/types/Item'
import { useCallback, useContext, useState } from 'react'

import { DeleteConfirmDialog } from '../../components/confirmDelete'
import { ItemTable } from '../../components/itemTable'
import { AuthContext } from '../../utils/authContext'
import { getBaseURL } from '../../utils/getBaseURL'

import { BinFormData, CreateBinForm } from './createBinForm'
import { CreateItemForm, ItemFormData } from './createItemForm'
import { SelectDestinationForm } from './selectDestinationForm'

const BinViewer = (props: { activeBin: Bin; allBins: Bin[]; items: Item[]; onReload?: () => void }) => {
  const [itemAddFormOpen, setItemAddFormOpen] = useState(false)
  const [binAddFormOpen, setBinAddFormOpen] = useState(false)
  const [binEditFormOpen, setBinEditFormOpen] = useState(false)
  const [selectBinMoveFromOpen, setSelectBinMoveFormOpen] = useState(false)
  const [selectItemMoveFromOpen, setSelectItemMoveFormOpen] = useState<string | undefined>(undefined)
  const [itemEditFromOpen, setItemEditFormOpen] = useState<Item | undefined>(undefined)

  const { tokenData } = useContext(AuthContext)

  const onReload = useCallback(() => {
    props.onReload?.()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.onReload])

  const saveItem = (data: ItemFormData) => {
    const formData = new FormData()
    Object.entries(data).forEach(([k, v]) => formData.append(k, v))
    formData.append('bin_id', props.activeBin.id)

    axios.post(`${getBaseURL()}/item/${data.id}`, formData).then(() => {
      setItemAddFormOpen(false)
      onReload()
    })
  }

  const deleteBin = (id: string) => {
    axios.delete(`${getBaseURL()}/bin/${id}`).then(() => {
      onReload()
    })
  }

  const deleteBinAndItems = (id: string) => {
    axios.delete(`${getBaseURL()}/bin/${id}/all`).then(() => {
      onReload()
    })
  }

  const saveBin = (data: BinFormData) => {
    axios
      .post(`${getBaseURL()}/bin/${data.id}`, {
        ...data,
        parent_id: props.activeBin.id,
      })
      .then(() => {
        setBinAddFormOpen(false)
        onReload()
      })
  }

  const editBin = (data: BinFormData) => {
    axios
      .put(`${getBaseURL()}/bin/${data.id}`, {
        ...data,
      })
      .then(() => {
        setBinEditFormOpen(false)
        onReload()
      })
  }

  const editItem = (data: ItemFormData) => {
    const formData = new FormData()
    Object.entries(data).forEach(([k, v]) => formData.append(k, v))
    formData.append('bin_id', props.activeBin.id)

    axios.put(`${getBaseURL()}/item/${data.id}`, formData).then(() => {
      setItemEditFormOpen(undefined)
      onReload()
    })
  }

  const moveBin = (id: string, to: string | undefined) => {
    axios
      .post(`${getBaseURL()}/bin/${id}/move`, {
        parent_id: to,
      })
      .then(() => {
        setSelectBinMoveFormOpen(false)
        onReload()
      })
  }

  const moveItem = (id: string, to: string | undefined) => {
    axios
      .post(`${getBaseURL()}/item/${id}/move`, {
        bin_id: to,
      })
      .then(() => {
        setSelectItemMoveFormOpen(undefined)
        onReload()
      })
  }

  const admin = tokenData && isAdmin(tokenData)

  return (
    <Box>
      <div style={{ display: 'flex', gap: 20 }}>
        <Typography variant="h4">
          {props.activeBin.parent_id ? 'Bin' : 'Tote'}: {props.activeBin.name}
        </Typography>
        {admin && (
          <>
            <IconButton onClick={() => setBinEditFormOpen(true)}>
              <Edit />
            </IconButton>
            <Tooltip arrow title="Move to a different bin">
              <IconButton onClick={() => setSelectBinMoveFormOpen(true)}>
                <DriveFileMove />
              </IconButton>
            </Tooltip>

            <Tooltip
              arrow
              title={`Delete bin ${!props.activeBin.parent_id ? 'and items' : 'only (move items to parent bin)'}`}
            >
              <DeleteConfirmDialog
                body={`Deleting will permanently delete this bin ${
                  !props.activeBin.parent_id ? 'and all items within it' : 'and move items to parent bin'
                }`}
                onDelete={() => deleteBin(props.activeBin.id)}
              >
                {(deleteAction: () => void) => (
                  <IconButton onClick={deleteAction} color="error">
                    {!props.activeBin.parent_id ? <DeleteForever /> : <Delete />}
                  </IconButton>
                )}
              </DeleteConfirmDialog>
            </Tooltip>
            {props.activeBin.parent_id && (
              <Tooltip arrow title="Delete bin and items">
                <DeleteConfirmDialog
                  body={'Deleting will permanently delete this bin and all items within it'}
                  onDelete={() => deleteBinAndItems(props.activeBin.id)}
                >
                  {(deleteAction: () => void) => (
                    <IconButton onClick={deleteAction} color="error">
                      <DeleteForever />
                    </IconButton>
                  )}
                </DeleteConfirmDialog>
              </Tooltip>
            )}
          </>
        )}
      </div>
      <div
        style={{
          marginTop: 10,
          padding: 10,
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
        }}
      >
        <Typography>{props.activeBin.description || 'No description provided'}</Typography>
      </div>
      <div
        style={{
          padding: 10,
          borderTop: '1px solid #DDD',
          borderBottom: '1px solid #DDD',
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          marginBottom: 25,
        }}
      >
        <Typography fontWeight="bold">Packing Instructions</Typography>
        <Typography>{props.activeBin.instructions || 'No packing instructions provided'}</Typography>
      </div>
      {admin && (
        <div style={{ display: 'flex', gap: 10 }}>
          <div style={{ flexGrow: 1 }}></div>
          <Tooltip arrow title="Create a new nested bin">
            <IconButton onClick={() => setBinAddFormOpen(true)}>
              <Inventory />
            </IconButton>
          </Tooltip>
          <Tooltip arrow title="Create a new item">
            <IconButton onClick={() => setItemAddFormOpen(true)}>
              <Category />
            </IconButton>
          </Tooltip>
        </div>
      )}
      <ItemTable
        editable={admin}
        items={props.items}
        setItemEditFormOpen={setItemEditFormOpen}
        setSelectItemMoveFormOpen={setSelectItemMoveFormOpen}
      />
      <CreateItemForm isOpen={itemAddFormOpen} onDismiss={() => setItemAddFormOpen(false)} onSave={saveItem} />
      <CreateItemForm
        isOpen={itemEditFromOpen !== undefined}
        onDismiss={() => setItemEditFormOpen(undefined)}
        data={itemEditFromOpen}
        editing
        onSave={editItem}
      />
      <CreateBinForm
        isOpen={binAddFormOpen}
        onDismiss={() => setBinAddFormOpen(false)}
        onSave={saveBin}
        parentBin={props.activeBin}
      />
      <CreateBinForm
        isOpen={binEditFormOpen}
        onDismiss={() => setBinEditFormOpen(false)}
        onSave={editBin}
        editing
        data={props.activeBin}
        parentBin={undefined}
      />
      <SelectDestinationForm
        isOpen={selectBinMoveFromOpen}
        onDismiss={() => setSelectBinMoveFormOpen(false)}
        binList={props.allBins.filter((b) => b.id !== props.activeBin.id)}
        onSave={(id) => {
          moveBin(props.activeBin.id, id)
        }}
      />
      <SelectDestinationForm
        isOpen={selectItemMoveFromOpen !== undefined}
        onDismiss={() => setSelectItemMoveFormOpen(undefined)}
        binList={props.allBins}
        onSave={(id) => {
          if (selectItemMoveFromOpen) {
            moveItem(selectItemMoveFromOpen, id)
          }
        }}
      />
    </Box>
  )
}

export { BinViewer }
